import { Injectable, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationService } from '../notification.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { ErrorLoggerService } from '../error/error-logger.service';
import { Subscription } from '../../models';
import { supabase } from './supabase.client';

interface SubscriptionRow {
  id: string;
  practice_id: string;
  payer_user_id: string | null;
  status: Subscription['status'];
  plan: string | null;
  current_period_end: string | null;
  grace_period_ends_at: string | null;
  payfast_token: string | null;
  voucher_id: string | null;
  created_at: string;
  updated_at: string;
}

const SELECT_COLS =
  'id, practice_id, payer_user_id, status, plan, current_period_end, grace_period_ends_at, payfast_token, voucher_id, created_at, updated_at';

const mapRow = (r: SubscriptionRow): Subscription => ({
  id: r.id,
  practiceId: r.practice_id,
  payerUserId: r.payer_user_id,
  status: r.status,
  plan: r.plan,
  currentPeriodEnd: r.current_period_end,
  gracePeriodEndsAt: r.grace_period_ends_at,
  payfastToken: r.payfast_token,
  voucherId: r.voucher_id,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private readonly auth = inject(AuthService);
  private readonly errorLogger = inject(ErrorLoggerService);
  private readonly router = inject(Router);
  private readonly notify = inject(NotificationService);

  readonly subscription = signal<Subscription | null>(null);
  readonly loading = signal(false);

  constructor() {
    // Reset + reload on practice switch.
    effect(() => {
      const pid = this.auth.practiceId();
      if (!pid) {
        this.subscription.set(null);
        return;
      }
      void this.refresh();
    });

    // Refetch on every top-level route navigation so the banner picks up
    // out-of-band status flips from the external billing app.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        void this.refresh();
      });
  }

  async getCurrent(): Promise<Subscription | null> {
    const pid = this.auth.practiceId();
    if (!pid) return null;

    const { data, error } = await supabase
      .from('subscriptions')
      .select(SELECT_COLS)
      .eq('practice_id', pid)
      .maybeSingle();

    if (error) {
      this.errorLogger.log(error, undefined, { context: 'SubscriptionService.getCurrent' });
      throw error;
    }
    return data ? mapRow(data as SubscriptionRow) : null;
  }

  async refresh(): Promise<void> {
    if (!this.auth.practiceId()) return;
    this.loading.set(true);
    try {
      this.subscription.set(await this.getCurrent());
    } catch {
      // already logged in getCurrent
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Open the external Manage Subscription site if configured; otherwise show a
   * "coming soon" snackbar. Used by the banner CTA, the avatar menu item, and
   * the Billing tab button.
   */
  openManageUrl(): void {
    const url = environment.manageSubscriptionUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      this.notify.info('Manage subscription — coming soon');
    }
  }
}
